import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import moment from "moment";
import $ from "jquery";
import * as action from "../../Action/Banner/Banner_Action";
import Model from "../Model/Index";
import CustomImageBox from "../UploadPreview/CustomImageBox";
import Loader from "../Loader";
import BannerVideoPreview from "./BannerVideoPreview";
import BannerTranscodingProgress from "./BannerTranscodingProgress";
import "./BannerManagement.css";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { usePermission } from "../../hooks/usePermission";
import { PERM } from "../../config/permissions";
import AuditTrail, { AuditTrailStyles } from "../AuditTrail";

const isBannerVideoReady = (banner) => banner?.transcoding_status === "complete"
  && banner?.hls_url && banner?.transcoding_job_id;

async function confirmVideoReview() {
  const result = await Swal.fire({
    title: "Activate banner?",
    input: "checkbox",
    inputPlaceholder: "I have previewed and verified this banner video",
    inputValidator: value => !value ? "Verify the video before activating." : undefined,
    showCancelButton: true,
    confirmButtonText: "Activate",
    confirmButtonColor: "var(--first-color)",
    cancelButtonColor: "var(--error-color)",
    customClass: { popup: "banner-activation-popup" },
  });
  return result.isConfirmed && result.value === 1;
}

const BannerManagement = () => {
  const dispatch = useDispatch();
  const { canCreate, canEdit, canDelete } = usePermission();
  const allowCreate = canCreate(PERM.BANNER_MANAGEMENT);
  const allowEdit = canEdit(PERM.BANNER_MANAGEMENT);
  const allowDelete = canDelete(PERM.BANNER_MANAGEMENT);
  const { loading: bannersLoading } = useSelector(
    (state) => state.GetBannersReducer,
  );

  const [activeTab, setActiveTab] = useState("banners"); // 'banners' or 'ranking'
  const [banners, setBanners] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showModal, setShowModal] = useState({
    show: false,
    type: "Add",
    data: null,
  });
  const [bannerForm, setBannerForm] = useState({
    series_id: "",
    title: "",
    image_url: "",
    banner_url: "",
    title_url: "",
    used_series_trailer: "Y",
    series_trailer_duration: 20,
    hls_url: "",
    rank: "",
    is_active: false,
  });
  const videoInputRef = useRef(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoS3Key, setVideoS3Key] = useState("");
  const [videoProgress, setVideoProgress] = useState(null);
  const [imageFiles, setImageFiles] = useState({});
  const [imagePreviews, setImagePreviews] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transcodingJob, setTranscodingJob] = useState(null);
  const isTranscoding = showModal.data?.transcoding_status === "processing";
  const videoChanging = showModal.type === "Add" || Boolean(videoFile || videoS3Key)
    || String(bannerForm.series_id) !== String(showModal.data?.series_id)
    || (bannerForm.used_series_trailer === "Y" && (
      showModal.data?.used_series_trailer !== "Y" || !showModal.data?.raw_video
      || Number(bannerForm.series_trailer_duration) !== Number(showModal.data?.series_trailer_duration ?? 20)
    ));
  const canActivate = !videoChanging && isBannerVideoReady(showModal.data);

  const [rankingBanners, setRankingBanners] = useState([]);
  const [originalRankingBanners, setOriginalRankingBanners] = useState([]);

  // Search & Filter State
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const searchTimeoutRef = useRef(null);

  // Series Selection inside modal state
  const [seriesSearchText, setSeriesSearchText] = useState("");
  const [seriesResults, setSeriesResults] = useState([]);
  const [seriesSearchLoading, setSeriesSearchLoading] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState(null);

  const dedupeById = (rows) => {
    return [...new Map((rows || []).map((item) => [item.id, item])).values()];
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchBanners(1, searchInput, statusFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fetchBanners = (currentPage = page, searchVal = searchInput, statusVal = statusFilter) => {
    const params = {
      // Backend page-size handling is unreliable (returns as few as 4 records
      // regardless of the requested limit), so fetch everything matching the
      // filters and paginate 12-per-page on the client instead.
      page: 1,
      limit: 300,
      per_page: 300,
      perPage: 300
    };
    if (searchVal.trim()) {
      params.search = searchVal.trim();
    }
    if (statusVal && statusVal !== "all") {
      params.status = statusVal;
    }
    dispatch(action.getBanners(params))
      .then((res) => {
        const unique = dedupeById(res?.responseDetails || []);
        setTotalRecords(unique.length);

        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;
        setBanners(unique.slice(startIndex, endIndex));
      })
      .catch((err) => {
        console.error(err);
        setBanners([]);
        setTotalRecords(0);
      });
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchBanners(1, val, statusFilter);
    }, 400);
  };

  const handleStatusFilterChange = (e) => {
    const val = e.target.value;
    setStatusFilter(val);
    setPage(1);
    fetchBanners(1, searchInput, val);
  };

  const handleSeriesSearch = (query) => {
    setSeriesSearchText(query);
    if (!query.trim()) {
      setSeriesResults([]);
      return;
    }
    setSeriesSearchLoading(true);
    dispatch(action.searchSeriesForBanner({ page: 1, limit: 200, search: query }))
      .then((res) => {
        setSeriesResults(res?.data || []);
      })
      .catch((err) => {
        console.error(err);
        setSeriesResults([]);
      })
      .finally(() => {
        setSeriesSearchLoading(false);
      });
  };

  const handleSelectSeries = (series) => {
    if (series.hasHeroBanner) return;
    setVideoFile(null);
    setVideoS3Key("");
    setVideoProgress(null);
    setSelectedSeries(series);
    setBannerForm({
      ...bannerForm,
      series_id: series.id,
      title: series.title,
      image_url: "",
      banner_url: "",
      title_url: "",
      used_series_trailer: "Y",
      series_trailer_duration: 20,
      hls_url: "",
      rank: banners.length + 1,
      is_active: false,
    });
    setImagePreviews({});
    setImageFiles({});
  };

  const handleOpenModal = (type, data = null) => {
    setShowModal({ show: true, type, data });
    setTranscodingJob(data?.transcoding_status === "processing" ? { id: data.id, jobId: data.transcoding_job_id } : null);
    setVideoFile(null);
    setVideoS3Key("");
    setVideoProgress(null);
    setSelectedSeries(null);
    setSeriesSearchText("");
    setSeriesResults([]);
    
    if (type === "Edit" && data) {
      setBannerForm({
        series_id: data.series_id,
        title: data.series_title || data.title || "",
        image_url: data.image_url || "",
        banner_url: data.banner_url || "",
        title_url: data.title_url || "",
        used_series_trailer: data.used_series_trailer || "Y",
        series_trailer_duration: data.series_trailer_duration || 20,
        hls_url: data.hls_url || "",
        rank: data.rank,
        is_active: data.is_active === 1 || data.is_active === true,
      });
      setImagePreviews({
        image_url: data.image_url || "",
        banner_url: data.banner_url || "",
        title_url: data.title_url || "",
      });
      setImageFiles({});
    } else {
      setBannerForm({
        series_id: "",
        title: "",
        image_url: "",
        banner_url: "",
        title_url: "",
        used_series_trailer: "Y",
        series_trailer_duration: 20,
        hls_url: "",
        rank: banners.length + 1,
        is_active: false,
      });
      setImagePreviews({});
      setImageFiles({});
    }
    $("body").addClass("no_scroll");
    $(".overlay").addClass("qv_active");
  };

  const handleCloseModal = () => {
    setTranscodingJob(null);
    setShowModal({ show: false, type: "Add", data: null });
    setVideoFile(null);
    setVideoS3Key("");
    setVideoProgress(null);
    setSelectedSeries(null);
    setSeriesSearchText("");
    setSeriesResults([]);
    setBannerForm({
      series_id: "",
      title: "",
      image_url: "",
      banner_url: "",
      title_url: "",
      used_series_trailer: "Y",
      series_trailer_duration: 20,
      hls_url: "",
      rank: "",
      is_active: false,
    });
    setImagePreviews({});
    setImageFiles({});
    $("body").removeClass("no_scroll");
    $(".overlay").removeClass("qv_active");
  };

  const handleImageChange = (e, file, preview) => {
    const field = e.target.name;
    setImageFiles((prev) => ({ ...prev, [field]: file }));
    setImagePreviews((prev) => ({ ...prev, [field]: preview || bannerForm[field] || "" }));
  };

  const handleVideoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/\.mp4$/i.test(file.name) || (file.type && file.type !== "video/mp4")) {
      toast.error("Banner video must be an MP4 file");
      event.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024 * 1024) {
      toast.error("Banner video must be at most 2 GB");
      event.target.value = "";
      return;
    }
    setVideoFile(file);
    setVideoS3Key("");
    setVideoProgress(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || isTranscoding) return;

    const imageUrls = {
      image_url: bannerForm.image_url || null,
      banner_url: bannerForm.banner_url || null,
      title_url: bannerForm.title_url || null,
    };

    if ((!imageUrls.banner_url && !imageFiles.banner_url) || (!imageUrls.title_url && !imageFiles.title_url)) {
      toast.error("Both Banner Image and Title Image are required");
      return;
    }

    const hasSavedUpload = showModal.type === "Edit"
      && showModal.data?.used_series_trailer !== "Y" && showModal.data?.raw_video;
    if (bannerForm.used_series_trailer !== "Y" && !videoFile && !videoS3Key && !hasSavedUpload) {
      toast.error("Banner video is required. Use the original trailer or upload an MP4.");
      return;
    }

    const trailerDuration = Number(bannerForm.series_trailer_duration);
    if (!Number.isInteger(trailerDuration) || trailerDuration < 1 || trailerDuration > 2147483647) {
      toast.error("Trailer duration must be a positive whole number of seconds");
      return;
    }

    let reviewedJobId;
    if (!videoChanging && bannerForm.is_active && !showModal.data?.is_active) {
      if (!canActivate) {
        toast.error("Banner video must finish transcoding before activation");
        return;
      }
      if (!await confirmVideoReview()) return;
      reviewedJobId = showModal.data.transcoding_job_id;
    }

    setIsSubmitting(true);
    try {
      for (const field of ["image_url", "banner_url", "title_url"]) {
        if (!imageFiles[field]) continue;
        const formData = new FormData();
        formData.append("image", imageFiles[field]);
        const res = await dispatch(action.uploadBannerImage(formData));
        const url = res?.data?.image_url || res?.responseDetails?.image_url || res?.image_url;
        if (!url) throw new Error("Upload failed");
        imageUrls[field] = url;
        setBannerForm((prev) => ({ ...prev, [field]: url }));
        setImageFiles((prev) => ({ ...prev, [field]: null }));
      }
    } catch (err) {
      toast.error("Image upload failed");
      setIsSubmitting(false);
      return;
    }

    let rawVideo = videoS3Key;
    if (videoFile && !rawVideo && bannerForm.used_series_trailer === "N") {
      try {
        setVideoProgress(0);
        rawVideo = await dispatch(action.uploadBannerVideo(videoFile, setVideoProgress));
        setVideoS3Key(rawVideo);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Video upload failed");
        setIsSubmitting(false);
        setVideoProgress(null);
        return;
      }
    }

    const payload = {
      used_series_trailer: bannerForm.used_series_trailer,
      series_trailer_duration: trailerDuration,
      ...(rawVideo && bannerForm.used_series_trailer === "N" ? { raw_video: rawVideo } : {}),
      series_id: parseInt(bannerForm.series_id),
      title: bannerForm.title,
      subtitle: bannerForm.title,
      ...imageUrls,
      cta_label: "Watch Now",
      cta_url: `/series/${bannerForm.series_id}`,
      position: parseInt(bannerForm.rank) || 1,
      is_active: !videoChanging && Boolean(bannerForm.is_active),
      ...(reviewedJobId ? { reviewed_job_id: reviewedJobId } : {}),
    };

    try {
      const creating = showModal.type === "Add";
      const response = await dispatch(creating
        ? action.createBanner(payload) : action.patchBanner(showModal.data.id, payload));
      const saved = response?.data || response?.responseDetails || {};
      const banner = { ...showModal.data, ...payload, ...saved };
      fetchBanners(creating ? 1 : page, searchInput, statusFilter);
      setActiveTab("banners");
      if (banner.id && ['processing', 'failed'].includes(banner.transcoding_status)) {
        // Switch new banners to edit mode immediately to avoid duplicate creation.
        setShowModal({ show: true, type: "Edit", data: banner });
        setBannerForm(prev => ({ ...prev, ...imageUrls, is_active: false, hls_url: "" }));
        setVideoFile(null);
        setVideoS3Key("");
        setVideoProgress(null);
        setImageFiles({});
        setTranscodingJob({ id: banner.id, jobId: banner.transcoding_job_id });
      } else {
        toast.success(creating ? "Banner created successfully" : "Banner updated successfully");
        handleCloseModal();
      }
    } catch (err) {
      toast.error(err?.message || err?.responseMessage || "Failed to save banner");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTranscodingFinished = (result) => {
    setShowModal(prev => ({ ...prev, data: {
      ...prev.data, ...result, transcoding_status: result.status,
    } }));
    setBannerForm(prev => ({ ...prev, hls_url: result.hls_url || "", is_active: Boolean(result.is_active) }));
    fetchBanners(page, searchInput, statusFilter);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--first-color)",
      cancelButtonColor: "var(--error-color)",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(action.deleteBanner(id))
          .then(() => {
            toast.success("Banner deleted");
            fetchBanners(page, searchInput, statusFilter);
          })
          .catch(() => {
            toast.error("Failed to delete banner");
          });
      }
    });
  };

  const toggleStatus = async (banner) => {
    const newStatus = !banner.is_active;
    if (newStatus) {
      if (!isBannerVideoReady(banner)) {
        toast.error("Banner video must finish transcoding before activation");
        return;
      }
      if (!await confirmVideoReview()) return;
    }
    const payload = {
      title: banner.series_title || banner.title,
      image_url: banner.image_url,
      is_active: newStatus,
      ...(newStatus ? { reviewed_job_id: banner.transcoding_job_id } : {}),
    };

    dispatch(action.patchBanner(banner.id, payload))
      .then(() => {
        toast.success(`Banner ${newStatus ? "activated" : "deactivated"}`);
        if (statusFilter !== "all") {
          fetchBanners(page, searchInput, statusFilter);
        } else {
          setBanners((prev) =>
            prev.map((b) =>
              b.id === banner.id ? { ...b, is_active: newStatus } : b,
            ),
          );
        }
      })
      .catch((err) => {
        toast.error(err?.responseMessage || "Failed to update status");
      });
  };

  useEffect(() => {
    if (activeTab === "ranking") {
      dispatch(action.getBanners({ page: 1, limit: 1000, status: "active" }))
        .then((res) => {
          const totalPages = res?.pagination?.totalPages || 1;
          const firstPageBanners = res?.responseDetails || [];
          
          if (totalPages > 1) {
            const promises = [];
            for (let p = 2; p <= totalPages; p++) {
              promises.push(dispatch(action.getBanners({ page: p, limit: 1000, status: "active" })));
            }
            Promise.all(promises)
              .then((results) => {
                const allBanners = [...firstPageBanners];
                results.forEach((r) => {
                  allBanners.push(...(r?.responseDetails || []));
                });
                const deduped = dedupeById(allBanners);
                setRankingBanners(deduped);
                setOriginalRankingBanners(deduped);
              })
              .catch((err) => {
                console.error(err);
                const deduped = dedupeById(firstPageBanners);
                setRankingBanners(deduped);
                setOriginalRankingBanners(deduped);
              });
          } else {
            const deduped = dedupeById(firstPageBanners);
            setRankingBanners(deduped);
            setOriginalRankingBanners(deduped);
          }
        })
        .catch((err) => {
          console.error(err);
          setRankingBanners([]);
          setOriginalRankingBanners([]);
        });
    }
  }, [activeTab, dispatch]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setRankingBanners((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveRanking = () => {
    const reorderData = rankingBanners.map((b, i) => ({
      id: b.id,
      position: i + 1,
    }));

    setIsSubmitting(true);
    dispatch(action.reorderBanners({ items: reorderData }))
      .then(() => {
        toast.success("Ranking updated successfully");
        setPage(1);
        fetchBanners(1);
        setActiveTab("banners");
      })
      .catch((err) => {
        toast.error(err?.responseMessage || "Failed to update ranking");
      })
      .finally(() => setIsSubmitting(false));
  };

  const hasUnsavedRankingChanges =
    JSON.stringify(rankingBanners.map((b) => b.id)) !==
    JSON.stringify(originalRankingBanners.map((b) => b.id));

  const handleResetRanking = () => {
    setRankingBanners(originalRankingBanners);
  };

  const goToBannersTab = () => {
    setActiveTab("banners");
    setPage(1);
    fetchBanners(1, searchInput, statusFilter);
  };

  const handleBannersTabClick = () => {
    if (activeTab === "ranking" && hasUnsavedRankingChanges) {
      Swal.fire({
        title: "Unsaved Display Order Changes",
        html: "You've reordered the banners but haven't saved yet.<br/>Switching tabs now will <strong>discard these changes</strong>.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Save Changes",
        cancelButtonText: "Cancel",
        confirmButtonColor: "var(--first-color)",
        cancelButtonColor: "var(--error-color)",
        reverseButtons: true,
        focusCancel: true,
      }).then((result) => {
        if (result.isConfirmed) {
          handleSaveRanking();
        }
        // Cancel/dismiss: stay on the Display Order tab, changes are kept
      });
      return;
    }
    goToBannersTab();
  };

  const SortableItem = ({ banner }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: banner.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="ranking-item"
      >
        <div className="d-flex align-items-center w-100">
          <div className="rank-handle me-3">
            <i className="fas fa-grip-vertical"></i>
          </div>
          <div className="rank-preview me-3">
            <img src={banner.banner_url || banner.image_url} alt="" />
          </div>
          <div className="rank-info">
            <div className="rank-title">{banner.series_title}</div>
            <div className="rank-meta">Rank: #{rankingBanners.indexOf(banner) + 1}</div>
          </div>
          <div className="ms-auto rank-badge">#{rankingBanners.indexOf(banner) + 1}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="banner-management">
      <AuditTrailStyles />
      <div className="overlay"></div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === "banners" ? "active" : ""}`}
            onClick={handleBannersTabClick}
          >
            All Banners ({totalRecords})
          </button>
          <button
            className={`tab-btn ${activeTab === "ranking" ? "active" : ""}`}
            onClick={() => setActiveTab("ranking")}
          >
            Display Order
          </button>
        </div>

        {activeTab === "banners" && (
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="search-box-premium">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Search series name..."
                value={searchInput}
                onChange={handleSearchChange}
                className="form-control premium-search-input"
              />
            </div>
            
            <div className="filter-box-premium">
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                className="form-control premium-select-filter"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {allowCreate && (
              <button
                className="btn btn-primary glow-btn"
                onClick={() => handleOpenModal("Add", null)}
              >
                <i className="fas fa-plus me-2"></i>New Banner
              </button>
            )}
          </div>
        )}
      </div>

      {activeTab === "banners" ? (
        <div className="banners-list">
          {bannersLoading ? (
            <Loader />
          ) : banners.length > 0 ? (
            <>
              <div className="row">
                {banners.map((banner, index) => (
                  <div
                    className="col-xl-3 col-lg-4 col-md-6 mb-3"
                    key={banner.id}
                  >
                    <div className="banner-card">
                      <div className="banner-img-container">
                        <img
                          src={banner.banner_url || banner.image_url}
                          alt="Thumbnail"
                          className="banner-img"
                        />
                        <div className="banner-status-badge">
                          <span
                            className={`badge-premium ${banner.is_active ? "active" : "inactive"}`}
                          >
                            {banner.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div className="banner-info p-3">
                        <h5 className="mb-1">{banner.series_title}</h5>
                        <p className="text-muted small mb-3">
                          Created: {moment(banner.created_at).format("ll")}
                        </p>
                        <div className="d-flex justify-content-between align-items-center mt-auto">
                          <div className="visibility-toggle">
                            <span className="toggle-label">Visible</span>
                            <label className="switch-premium">
                              <input
                                type="checkbox"
                                checked={banner.is_active}
                                disabled={!banner.is_active && !isBannerVideoReady(banner)}
                                disabled={!allowEdit}
                                onChange={() => toggleStatus(banner)}
                              />
                              <span className="slider-premium round"></span>
                            </label>
                          </div>
                          <div className="action-btns">
                            {allowEdit && (
                              <button
                                className="btn btn-sm btn-info me-2"
                                onClick={() => handleOpenModal("Edit", banner)}
                                title="Edit"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {allowDelete && (
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(banner.id)}
                                title="Delete"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </div>

                        <AuditTrail record={banner} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {Math.ceil(totalRecords / limit) > 1 && (
                <div className="pagination-container mt-4 d-flex justify-content-center align-items-center gap-2">
                  <button
                    className="btn btn-pagination"
                    disabled={page === 1}
                    onClick={() => {
                      const newPage = page - 1;
                      setPage(newPage);
                      fetchBanners(newPage, searchInput, statusFilter);
                    }}
                  >
                    <i className="fas fa-chevron-left me-1"></i> Prev
                  </button>
                  <span className="mx-2" style={{ color: "#a0a0b0" }}>
                    Page <strong className="text-white">{page}</strong> of <strong className="text-white">{Math.ceil(totalRecords / limit)}</strong>
                  </span>
                  <button
                    className="btn btn-pagination"
                    disabled={page === Math.ceil(totalRecords / limit)}
                    onClick={() => {
                      const newPage = page + 1;
                      setPage(newPage);
                      fetchBanners(newPage, searchInput, statusFilter);
                    }}
                  >
                    Next <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-5 bg-dark rounded">
              <i className="fas fa-image fa-3x mb-3 text-muted"></i>
              <h5>No banners found</h5>
              <p className="text-muted">
                No banners matched your search or status filter. Click 'New Banner' to add one.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="ranking-tab-content">
          <div className="ranking-header-clean mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h4 className="text-white mb-1">Banner Display Order</h4>
                <p className="text-muted small mb-0">
                  Drag and drop series to change their appearance order on the
                  homepage hero section. Only active banners participate in display order.
                </p>
              </div>
            </div>
          </div>

          {rankingBanners.length > 0 ? (
            <>
              <div className="ranking-list-grid">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={rankingBanners.map((b) => b.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {rankingBanners.map((banner) => (
                      <SortableItem key={banner.id} banner={banner} />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>

              {hasUnsavedRankingChanges && (
                <div className="floating-save-container d-flex gap-3">
                  <button
                    className="btn btn-cancel floating-reset-btn"
                    onClick={handleResetRanking}
                    disabled={isSubmitting}
                  >
                    <i className="fas fa-undo me-2"></i>
                    Reset Rank
                  </button>
                  <button
                    className="btn btn-primary glow-btn floating-save-btn"
                    onClick={handleSaveRanking}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <i className="fas fa-spinner fa-spin me-2"></i>
                    ) : (
                      <i className="fas fa-save me-2"></i>
                    )}
                    {isSubmitting ? "Saving..." : "Save New Sequence"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-5 bg-dark rounded">
              <i className="fas fa-sort-amount-down fa-3x mb-3 text-muted"></i>
              <h5>No active banners to order</h5>
              <p className="text-muted">
                Activate banners in the 'All Banners' tab to set their display sequence.
              </p>
            </div>
          )}
        </div>
      )}

      {showModal.show && (
        <Model className="common-popup edit-popup active banner-editor">
          <div className="edit-heading d-flex align-items-center justify-content-between">
            <h3>{showModal.type} Hero Banner</h3>
            <button
              className="common-close edit-close-btn"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="edit-inner form-img-upload">
            {transcodingJob && <BannerTranscodingProgress bannerId={transcodingJob.id}
              jobId={transcodingJob.jobId} onFinished={handleTranscodingFinished} />}
            <form onSubmit={handleSubmit}>
              <fieldset disabled={isSubmitting || isTranscoding}>
              <div className="d-flex flex-column gap-3">
                <div className="banner-details-row">
                  <div>
                {showModal.type === "Add" ? (
                  !selectedSeries ? (
                    <div className="form-group mb-4">
                      <label className="form-label">Search & Select Series</label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control premium-input"
                          placeholder="Type series name to search..."
                          value={seriesSearchText}
                          onChange={(e) => handleSeriesSearch(e.target.value)}
                        />
                        {seriesSearchLoading && (
                          <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                            <span className="spinner-border spinner-border-sm text-info"></span>
                          </div>
                        )}
                      </div>
                      {seriesResults.length > 0 && (
                        <ul className="series-search-results-list">
                          {seriesResults.map((series) => (
                            <li
                              key={series.id}
                              className={`series-search-item ${series.hasHeroBanner ? "disabled" : ""}`}
                              onClick={() => handleSelectSeries(series)}
                            >
                              <img src={series.thumbnail} alt="" />
                              <div className="series-search-title">{series.title}</div>
                              {series.hasHeroBanner ? (
                                <span className="series-search-badge has-banner">Already has banner</span>
                              ) : (
                                <span className="series-search-badge">Select</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <div className="form-group mb-4">
                      <label className="form-label">Selected Series</label>
                      <div className="selected-series-banner-info">
                        <div className="selected-series-details">
                          <img src={selectedSeries.thumbnail} alt="" />
                          <div>
                            <h5>{selectedSeries.title}</h5>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-change-series"
                          onClick={() => {
                            setSelectedSeries(null);
                            setBannerForm({ ...bannerForm, series_id: "" });
                          }}
                        >
                          Change Series
                        </button>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="form-group mb-4">
                    <label className="form-label">Series</label>
                    <input
                      type="text"
                      className="form-control premium-input disabled"
                      value={
                        showModal.data?.series_title ||
                        showModal.data?.title ||
                        ""
                      }
                      disabled
                    />
                  </div>
                )}
                  </div>
                    <div className="form-group">
                      <span className="form-label d-block">Status</span>
                      <label className="banner-trailer-checkbox">
                        <input
                          id="banner-status"
                          type="checkbox"
                          aria-label="Status"
                          checked={!videoChanging && Boolean(bannerForm.is_active)}
                          disabled={isSubmitting || videoChanging || (!bannerForm.is_active && !canActivate)}
                          onChange={(e) => setBannerForm({ ...bannerForm, is_active: e.target.checked })}
                        />
                        <span>{!videoChanging && bannerForm.is_active ? "Active" : "Inactive"}</span>
                      </label>
                    </div>
                </div>

                {(showModal.type === "Edit" || selectedSeries) && (
                  <>
                    <section className="banner-media-box" aria-labelledby="banner-images-heading">
                      <h5 id="banner-images-heading">Images</h5>
                      <div className="banner-images-row">
                    {[
                      { field: "banner_url", label: "Banner Image *" },
                      { field: "title_url", label: "Title Image *" },
                      { field: "image_url", label: "Old Banner Image" },
                    ].map(({ field, label }) => (
                      <div key={field}>
                        <div className="form-group">
                          <CustomImageBox
                            imageUrl={imagePreviews[field] || "https://flixy.retrytech.site/assets/img/placeholder-image.png"}
                            onChange={handleImageChange}
                            label={label}
                            name={field}
                            maxSize={1024 * 1024}
                            hint={field === "image_url" ? <><span className="badge bg-secondary">Obsolete</span> · Max 1 MB</> : "WebP · Max 1 MB"}
                            editLabel="Click to edit"
                            className={`banner-image-preview${field === "image_url" ? " banner-image-preview-old" : ""}`}
                          />
                          {isSubmitting && imageFiles[field] && (
                            <div className="mt-2 text-info small">Uploading assets...</div>
                          )}
                        </div>
                      </div>
                    ))}
                      </div>
                    </section>
                    <section className="banner-media-box" aria-labelledby="banner-video-heading">
                      <h5 id="banner-video-heading">Banner video *</h5>
                        <div className="banner-video-choices">
                        <div className="banner-source-option" aria-disabled={Boolean(videoFile)}>
                        <div className="banner-trailer-options">
                          <label htmlFor="used-series-trailer" className="banner-trailer-checkbox">
                            <input
                              id="used-series-trailer"
                              type="checkbox"
                              checked={bannerForm.used_series_trailer === "Y"}
                              disabled={isSubmitting || Boolean(videoFile)}
                              onChange={(e) => {
                                setBannerForm({ ...bannerForm, used_series_trailer: e.target.checked ? "Y" : "N" });
                                setVideoFile(null);
                                setVideoS3Key("");
                                setVideoProgress(null);
                              }}
                            />
                            <span>Use original trailer</span>
                          </label>
                          <div>
                            <label htmlFor="series-trailer-duration" className="form-label">Clip duration from start (seconds)</label>
                            <input
                              id="series-trailer-duration"
                              aria-describedby="series-trailer-duration-hint"
                              type="number"
                              min="1"
                              max="2147483647"
                              step="1"
                              className="form-control premium-input"
                              value={bannerForm.series_trailer_duration}
                              disabled={isSubmitting || bannerForm.used_series_trailer !== "Y"}
                              onChange={(e) => setBannerForm({ ...bannerForm, series_trailer_duration: e.target.value })}
                            />
                            <small id="series-trailer-duration-hint" className="d-block mt-1" style={{ color: 'var(--body-text-color)', fontSize: 11 }}>
                              20 seconds uses the video from 0:00 to 0:20.
                            </small>
                          </div>
                        </div>

                        </div>
                        <div className="banner-video-or"><span>OR</span></div>
                        <div className="banner-source-option" aria-disabled={bannerForm.used_series_trailer === "Y"}>
                        <label htmlFor="banner-video" className="form-label">Upload a new file</label>
                        <input
                          key={bannerForm.used_series_trailer}
                          ref={videoInputRef}
                          id="banner-video"
                          name="raw_video"
                          type="file"
                          accept=".mp4,video/mp4"
                          className="form-control premium-input"
                          disabled={isSubmitting || bannerForm.used_series_trailer === "Y"}
                          onChange={handleVideoChange}
                        />
                        <small className="d-block mt-1 text-muted">MP4 · High resolution · Max 2 GB</small>
                        {videoFile && (
                          <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              disabled={isSubmitting}
                              onClick={() => {
                                setVideoFile(null);
                                setVideoS3Key("");
                                setVideoProgress(null);
                                if (videoInputRef.current) videoInputRef.current.value = "";
                              }}
                            >Remove file</button>
                          </div>
                        )}
                        {videoProgress !== null && (
                          <div className="mt-2 text-info small" role="status">Video upload: {videoProgress}%</div>
                        )}
                        </div>
                        </div>
                        {(videoFile || bannerForm.hls_url) && <div className="mt-2">
                          <BannerVideoPreview bannerId={showModal.data?.id} file={videoFile} hlsUrl={bannerForm.hls_url} />
                        </div>}
                    </section>

                  </>
                )}
              </div>
              </fieldset>
              <div className="action-footer text-end mt-4">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary glow-btn"
                  disabled={isSubmitting || isTranscoding || (showModal.type === "Add" && !selectedSeries)}
                >
                  {isSubmitting ? (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  ) : null}
                  {isSubmitting ? "Processing..." : isTranscoding ? "Transcoding..." : "Save Banner"}
                </button>
              </div>
            </form>
          </div>
        </Model>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
                .banner-management .banner-trailer-options {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr);
                    align-items: end;
                    gap: 8px;
                }
                .banner-management .banner-trailer-checkbox {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-height: 44px;
                    margin: 0;
                }
                .banner-management .banner-trailer-checkbox input {
                    width: 18px;
                    height: 18px;
                    min-height: 0;
                    flex: 0 0 18px;
                    padding: 0;
                    margin: 0;
                    accent-color: var(--first-color);
                }
                .banner-management .banner-video-choices {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
                    gap: 12px;
                    align-items: stretch;
                }
                .banner-management .banner-video-or {
                    display: flex;
                    align-items: center;
                    color: var(--body-text-color);
                    font-size: 12px;
                }
                .banner-management .banner-details-row {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) 150px;
                    gap: 20px;
                    align-items: start;
                }
                .banner-management .banner-media-box {
                    border: 1px solid var(--border-color, #34353e);
                    border-radius: 12px;
                    padding: 12px;
                }
                .banner-management .banner-media-box > h5 {
                    margin-bottom: 10px;
                }
                .banner-management .banner-source-option {
                    border: 1px solid var(--border-color, #34353e);
                    border-radius: 8px;
                    padding: 10px;
                }
                .banner-management .banner-source-option[aria-disabled="true"] {
                    opacity: 0.55;
                }
                @media (max-width: 575px) {
                    .banner-management .banner-video-choices {
                        grid-template-columns: minmax(0, 1fr);
                    }
                    .banner-management .banner-video-or {
                        justify-content: center;
                    }
                }
                @media (max-width: 480px) {
                    .banner-management .banner-details-row {
                        grid-template-columns: minmax(0, 1fr);
                        gap: 0;
                    }
                    .banner-management .banner-media-box {
                        padding: 12px;
                    }
                    .banner-management .banner-trailer-options {
                        grid-template-columns: minmax(0, 1fr);
                        gap: 8px;
                    }
                }
                .banner-management {
                    padding: 20px;
                }
                .tabs-container {
                    display: flex;
                    gap: 10px;
                    background: rgba(255,255,255,0.05);
                    padding: 5px;
                    border-radius: 8px;
                }
                .tab-btn {
                    padding: 8px 20px;
                    border: none;
                    background: transparent;
                    color: #8a8a9a;
                    border-radius: 6px;
                    transition: all 0.3s;
                    font-weight: 500;
                }
                .tab-btn.active {
                    background: var(--first-color);
                    color: white;
                }
                .banner-card {
                    background: #11111a;
                    border: 1px solid rgba(255,255,255,0.05);
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    height: 100%;
                }
                .banner-card:hover {
                    transform: translateY(-2px);
                    border-color: rgba(0, 160, 233, 0.4);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.5);
                }
                .banner-img-container {
                    position: relative;
                    aspect-ratio: 3/2;
                    overflow: hidden;
                }
                .banner-img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.6s ease;
                }
                .banner-card:hover .banner-img {
                    transform: scale(1.1);
                }
                .banner-status-badge {
                    position: absolute;
                    top: 15px;
                    right: 15px;
                    z-index: 2;
                }
                .badge-premium {
                    padding: 4px 10px;
                    border-radius: 20px;
                    font-size: 9px;
                    text-transform: uppercase;
                    font-weight: 800;
                    letter-spacing: 0.5px;
                    backdrop-filter: blur(10px);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
                    border: 1px solid rgba(255,255,255,0.1);
                    display: inline-block;
                }
                .badge-premium.active {
                    background: rgba(0, 160, 233, 0.2);
                    color: #00d2ff;
                    border-color: rgba(0, 160, 233, 0.4);
                }
                .badge-premium.inactive {
                    background: rgba(220, 53, 69, 0.15);
                    color: #ff4d5a;
                    border-color: rgba(220, 53, 69, 0.3);
                }
                .banner-info {
                    padding: 20px !important;
                    background: linear-gradient(to bottom, #11111a, #0a0a14);
                }
                .banner-info h5 {
                    color: #fff;
                    font-weight: 800;
                    font-size: 20px;
                    margin-bottom: 8px !important;
                }
                .banner-info p {
                    color: #555 !important;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                .btn-premium-order {
                    background: rgba(255,255,255,0.03);
                    color: #8a8a9a;
                    border: 1px solid rgba(255,255,255,0.1);
                    padding: 8px 20px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                .btn-premium-order:hover {
                    background: rgba(255,255,255,0.06);
                    color: #fff;
                    transform: translateY(-2px);
                }
                .btn-premium-order.active {
                    background: linear-gradient(90deg, #00a0e9, #00d2ff);
                    color: white;
                    border: none;
                    box-shadow: 0 4px 15px rgba(0, 160, 233, 0.4);
                }
                .ranking-header-clean {
                    padding: 10px 0 25px 0;
                    margin-bottom: 10px !important;
                }
                .ranking-header-clean h4 {
                    font-weight: 800;
                    letter-spacing: 0.5px;
                }
                .ranking-header-clean p {
                    color: #8a8a9a !important;
                    font-size: 13px !important;
                    line-height: 1.6;
                    max-width: 600px;
                }
                .floating-save-container {
                    position: fixed;
                    bottom: 40px;
                    right: 40px;
                    z-index: 1000;
                    animation: slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                }
                @keyframes slideUp {
                    from { transform: translateY(100px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .floating-save-btn {
                    padding: 15px 35px !important;
                    border-radius: 50px !important;
                    font-size: 15px !important;
                    font-weight: 800 !important;
                    box-shadow: 0 10px 30px rgba(0, 160, 233, 0.5) !important;
                    border: 2px solid rgba(255,255,255,0.1) !important;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .floating-reset-btn {
                    padding: 15px 30px !important;
                    border-radius: 50px !important;
                    font-size: 15px !important;
                    font-weight: 700 !important;
                    background: #1a1a2e !important;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .floating-reset-btn:hover {
                    transform: translateY(-2px);
                    background: #22223a !important;
                }
                .floating-save-btn:hover {
                    transform: scale(1.05) translateY(-5px);
                    box-shadow: 0 15px 40px rgba(0, 160, 233, 0.7) !important;
                }
                .ranking-list-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 12px;
                    max-width: 850px;
                    padding-bottom: 100px;
                }
                .ranking-item {
                    background: #1a1a2e;
                    border: 1px solid rgba(255,255,255,0.05);
                    border-radius: 12px;
                    padding: 12px;
                    margin-bottom: 10px;
                    cursor: grab;
                    transition: all 0.2s;
                }
                .ranking-item:active {
                    cursor: grabbing;
                    border-color: #00a0e9;
                    box-shadow: 0 0 20px rgba(0, 160, 233, 0.2);
                }
                .rank-handle {
                    color: #444;
                    font-size: 18px;
                }
                .rank-preview {
                    width: 80px;
                    aspect-ratio: 16/9;
                    border-radius: 6px;
                    overflow: hidden;
                    border: 1px solid rgba(255,255,255,0.1);
                }
                .rank-preview img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .rank-title {
                    font-weight: 700;
                    color: #fff;
                    font-size: 15px;
                }
                .rank-meta {
                    font-size: 11px;
                    color: #00a0e9;
                    font-weight: 800;
                    margin-top: 2px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .rank-badge {
                    background: rgba(0, 160, 233, 0.1);
                    color: #00a0e9;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-weight: 800;
                    font-size: 14px;
                }
                .btn-cancel {
                    background: transparent;
                    color: #888;
                    border: 1px solid #333;
                    padding: 10px 25px;
                    border-radius: 10px;
                    font-weight: 600;
                    transition: all 0.3s;
                }
                .btn-cancel:hover {
                    color: #fff;
                    border-color: #555;
                }
                .btn-save-ranking {
                    background: linear-gradient(90deg, #00a0e9, #00d2ff);
                    color: #fff;
                    border: none;
                    padding: 10px 30px;
                    border-radius: 10px;
                    font-weight: 700;
                    box-shadow: 0 4px 15px rgba(0, 160, 233, 0.3);
                    transition: all 0.3s;
                }
                .btn-save-ranking:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(0, 160, 233, 0.5);
                }

                .visibility-toggle {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .toggle-label {
                    font-size: 11px;
                    font-weight: 700;
                    color: #555;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                /* Premium Switch Style */
                .switch-premium {
                    position: relative;
                    display: inline-block;
                    width: 38px;
                    height: 20px;
                }
                .switch-premium input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }
                .slider-premium {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: #222;
                    transition: .4s;
                    border: 1px solid rgba(255,255,255,0.1);
                }
                .slider-premium:before {
                    position: absolute;
                    content: "";
                    height: 14px;
                    width: 14px;
                    left: 2px;
                    bottom: 2px;
                    background-color: #888;
                    transition: .4s;
                }
                input:checked + .slider-premium {
                    background-color: rgba(0, 160, 233, 0.2);
                    border-color: rgba(0, 160, 233, 0.4);
                }
                input:checked + .slider-premium:before {
                    transform: translateX(18px);
                    background-color: #00a0e9;
                    box-shadow: 0 0 10px rgba(0, 160, 233, 0.5);
                }
                .slider-premium.round {
                    border-radius: 34px;
                }
                .slider-premium.round:before {
                    border-radius: 50%;
                }

                .action-btns .btn {
                    width: 36px;
                    height: 36px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 8px;
                    border: none;
                    transition: all 0.3s;
                    background: rgba(255,255,255,0.05);
                    color: #fff;
                }
                .action-btns .btn:hover {
                    background: var(--first-color);
                    color: #fff !important;
                    transform: translateY(-1px);
                    box-shadow: none !important;
                }
                .action-btns .btn:hover i {
                    color: #fff !important;
                }
                .action-btns .btn-info { background: rgba(0, 160, 233, 0.1); color: #00a0e9; }
                .action-btns .btn-danger { background: rgba(220, 53, 69, 0.1); color: #dc3545; }
                .action-btns .btn-danger:hover { background: #dc3545; color: #fff; }
                .eligible-list .row {
                    display: grid;
                    grid-template-columns: repeat(6, 1fr);
                    gap: 15px;
                }
                .col-custom-8 {
                    width: 100%;
                }
                @media (max-width: 1400px) {
                    .eligible-list .row { grid-template-columns: repeat(4, 1fr); }
                }
                @media (max-width: 1200px) {
                    .eligible-list .row { grid-template-columns: repeat(3, 1fr); }
                }
                @media (max-width: 768px) {
                    .eligible-list .row { grid-template-columns: repeat(2, 1fr); }
                }

                .series-card-vertical {
                    background: linear-gradient(165deg, #1a1a2e 0%, #121225 100%);
                    border: 1px solid rgba(0, 160, 233, 0.1);
                    border-radius: 14px;
                    overflow: hidden;
                    padding: 10px;
                    height: 330px;
                    display: flex;
                    flex-direction: column;
                    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                    position: relative;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.4);
                }
                .series-card-vertical:hover {
                    transform: translateY(-2px);
                    border-color: rgba(0, 160, 233, 0.4);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.5);
                    background: linear-gradient(165deg, #1e1e35 0%, #16162a 100%);
                }
                .card-poster-v {
                    width: 100%;
                    aspect-ratio: 9/16;
                    position: relative;
                    overflow: hidden;
                    border-radius: 10px;
                    border: 1px solid rgba(255,255,255,0.05);
                    background: #000;
                    margin-bottom: 12px;
                    box-shadow: inset 0 0 20px rgba(0,0,0,0.8);
                }
                .card-poster-v img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
                }
                .series-card-vertical:hover .card-poster-v img {
                    transform: scale(1.08);
                }
                .card-date-v {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    text-align: center;
                    background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
                    color: rgba(255,255,255,0.6);
                    font-size: 8px;
                    padding: 8px 0 4px;
                    font-weight: 500;
                }
                .card-content-v {
                    flex-grow: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    gap: 8px;
                }
                .card-title-v {
                    font-size: 13px;
                    font-weight: 700;
                    color: #fff;
                    margin: 0;
                    text-align: center;
                    display: -webkit-box;
                    -webkit-line-clamp: 1;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    line-height: 1.4;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
                }
                .btn-card-v {
                    width: 100%;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 0;
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                }
                .btn-card-v.add {
                    background: linear-gradient(90deg, #00a0e9, #00d2ff);
                    color: #fff;
                    box-shadow: 0 4px 15px rgba(0, 160, 233, 0.3);
                }
                .btn-card-v.add:hover {
                    background: linear-gradient(90deg, #00b4ff, #33e0ff);
                    box-shadow: 0 6px 20px rgba(0, 160, 233, 0.5);
                    transform: translateY(-1px);
                }
                .btn-card-v.disabled {
                    background: #222;
                    color: #555;
                    cursor: not-allowed;
                    box-shadow: none;
                }
                .btn-card-v.add {
                    background: var(--first-color);
                    color: #fff;
                }
                .btn-card-v.add:hover {
                    background: #0084c5;
                }
                .btn-card-v.disabled {
                    background: #333;
                    color: #777;
                    cursor: not-allowed;
                }
                .card-stats {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 2px;
                    margin-bottom: 8px;
                }
                .stat-item {
                    font-size: 10px;
                    color: #888;
                }
                .stat-item .val.blue {
                    color: #00a0e9;
                    font-weight: 600;
                }
                .card-toggles {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px 15px;
                    margin-bottom: 12px;
                }
                .toggle-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 11px;
                    color: #eee;
                }
                .custom-chk {
                    width: 14px;
                    height: 14px;
                    border: 1.5px solid #444;
                    border-radius: 3px;
                    position: relative;
                }
                .custom-chk.checked {
                    background: #00a0e9;
                    border-color: #00a0e9;
                }
                .custom-chk.checked::after {
                    content: '✓';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -55%);
                    color: white;
                    font-size: 9px;
                }
                .card-actions-row {
                    display: flex;
                    gap: 8px;
                }
                .btn-xs {
                    padding: 2px 8px;
                    font-size: 11px;
                    border-radius: 4px;
                }
                .premium-input {
                    background: #1a1a2e !important;
                    border: 1px solid #333 !important;
                    color: #fff !important;
                    border-radius: 8px !important;
                    padding: 10px 15px !important;
                }
                .premium-input:focus {
                    border-color: var(--first-color) !important;
                    box-shadow: 0 0 5px rgba(0, 160, 233, 0.2) !important;
                    background: #1e1e35 !important;
                }
                .premium-input.disabled {
                    background: #111 !important;
                    opacity: 0.7 !important;
                    cursor: not-allowed !important;
                }
                .glow-btn {
                    box-shadow: 0 0 15px rgba(0, 160, 233, 0.4);
                }
                .glow-btn:hover {
                    box-shadow: 0 0 20px rgba(0, 160, 233, 0.6);
                }
                .btn-card {
                    flex: 1;
                    border: none;
                    border-radius: 8px;
                    padding: 6px 0;
                    font-size: 11px;
                    font-weight: 600;
                    transition: opacity 0.2s;
                }
                .btn-card.view {
                    background: #0084c5;
                    color: white;
                }
                .btn-card.add {
                    background: #00a0e9;
                    color: white;
                    box-shadow: 0 0 10px rgba(0, 160, 233, 0.3);
                }
                .btn-card.add.disabled {
                    background: #333;
                    box-shadow: none;
                    cursor: not-allowed;
                }
                .btn-card:hover {
                    opacity: 0.9;
                }
                /* Absolute Override for Modal Preview */
                .compact-preview {
                    max-width: 250px !important;
                }
                .compact-preview img {
                    width: 100% !important;
                    max-height: 140px !important;
                    object-fit: contain !important;
                    background: #000 !important;
                    border-radius: 8px !important;
                    margin-top: 5px !important;
                    border: 1px solid #333 !important;
                }
                .compact-preview .upload-options {
                    margin-bottom: 5px !important;
                }
                 .edit-inner .form-group {
                    margin-bottom: 15px !important;
                }
                .pagination-container {
                    margin-top: 30px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 15px;
                }
                .btn-pagination {
                    background: rgba(255, 255, 255, 0.05) !important;
                    border: 1px solid rgba(255, 255, 255, 0.1) !important;
                    color: #8a8a9a !important;
                    padding: 8px 16px !important;
                    border-radius: 8px !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    transition: all 0.3s ease !important;
                }
                .btn-pagination:hover:not(:disabled) {
                    background: var(--first-color) !important;
                    color: white !important;
                    border-color: var(--first-color) !important;
                    box-shadow: 0 0 10px rgba(0, 160, 233, 0.4) !important;
                    transform: translateY(-1px);
                }
                .btn-pagination:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
                
                .search-box-premium {
                    position: relative;
                    min-width: 250px;
                }
                .search-box-premium .search-icon {
                    position: absolute;
                    left: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #555;
                    font-size: 14px;
                }
                .premium-search-input {
                    background: #1a1a2e !important;
                    border: 1px solid #333 !important;
                    color: #fff !important;
                    border-radius: 8px !important;
                    padding: 10px 15px 10px 40px !important;
                    font-size: 14px !important;
                    width: 100%;
                }
                .premium-search-input:focus {
                    border-color: var(--first-color) !important;
                    box-shadow: 0 0 5px rgba(0, 160, 233, 0.2) !important;
                }
                .premium-select-filter {
                    background: #1a1a2e !important;
                    border: 1px solid #333 !important;
                    color: #fff !important;
                    border-radius: 8px !important;
                    padding: 10px 30px 10px 15px !important;
                    font-size: 14px !important;
                    height: 44px;
                    cursor: pointer;
                }
                .premium-select-filter:focus {
                    border-color: var(--first-color) !important;
                    box-shadow: 0 0 5px rgba(0, 160, 233, 0.2) !important;
                }
                .series-search-results-list {
                    background: #11111a;
                    border: 1px solid #333;
                    border-radius: 8px;
                    max-height: 250px;
                    overflow-y: auto;
                    margin-top: 5px;
                    padding: 0;
                    list-style: none;
                }
                .series-search-item {
                    display: flex;
                    align-items: center;
                    padding: 10px 15px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    cursor: pointer;
                    transition: background 0.2s;
                }
                .series-search-item:hover:not(.disabled) {
                    background: rgba(0, 160, 233, 0.1);
                }
                .series-search-item.disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    background: rgba(255,255,255,0.02);
                }
                .series-search-item img {
                    width: 40px;
                    height: 40px;
                    object-fit: cover;
                    border-radius: 4px;
                    margin-right: 12px;
                }
                .series-search-title {
                    font-weight: 600;
                    color: #fff;
                    flex-grow: 1;
                }
                .series-search-badge {
                    background: rgba(255,255,255,0.08);
                    color: #888;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: bold;
                }
                .series-search-badge.has-banner {
                    background: rgba(0, 160, 233, 0.15);
                    color: #00a0e9;
                }
                .selected-series-banner-info {
                    background: rgba(0, 160, 233, 0.05);
                    border: 1px solid rgba(0, 160, 233, 0.2);
                    border-radius: 8px;
                    padding: 12px 15px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .selected-series-details {
                    display: flex;
                    align-items: center;
                }
                .selected-series-details img {
                    width: 50px;
                    height: 50px;
                    object-fit: cover;
                    border-radius: 4px;
                    margin-right: 15px;
                }
                .selected-series-details h5 {
                    color: #fff;
                    margin: 0;
                    font-weight: 700;
                }
                .btn-change-series {
                    background: transparent;
                    border: 1px solid #444;
                    color: #aaa;
                    padding: 5px 12px;
                    border-radius: 6px;
                    font-size: 12px;
                    transition: all 0.2s;
                }
                .btn-change-series:hover {
                    color: #fff;
                    border-color: #777;
                    background: rgba(255,255,255,0.05);
                }
            `,
        }}
      />
    </div>
  );
};

export default BannerManagement;
